import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  CalendarClock, Users, Stethoscope, Images, Newspaper, Star, Settings,
  LogOut, Plus, Pencil, Trash2, ExternalLink, Home,
} from "lucide-react";
import { api, fileUrl } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Logo } from "../../components/Logo";
import { UploadField, Modal, inputCls } from "./adminUtils";
import { ICON_OPTIONS } from "../../components/ServiceIcon";

const TABS = [
  { id: "appointments", label: "Appointments", icon: CalendarClock },
  { id: "doctors", label: "Doctors", icon: Users },
  { id: "services", label: "Services", icon: Stethoscope },
  { id: "gallery", label: "Gallery", icon: Images },
  { id: "testimonials", label: "Reviews", icon: Star },
  { id: "content", label: "Site Content", icon: Settings },
];

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("appointments");

  const doLogout = () => { logout(); navigate("/admin/login"); };

  return (
    <div className="min-h-screen bg-[#F0F7FB] flex flex-col lg:flex-row">
      {/* Sidebar */}
      <aside className="lg:w-64 bg-white border-r border-slate-100 lg:min-h-screen shrink-0">
        <div className="p-5 border-b border-slate-100"><Logo /></div>
        <nav className="p-3 flex lg:flex-col gap-1 overflow-x-auto">
          {TABS.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`admin-tab-${t.id}`}
              className={`flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-semibold whitespace-nowrap transition ${tab === t.id ? "gradient-bg text-white" : "text-[#334E68] hover:bg-slate-50"}`}>
              <t.icon size={18} /> {t.label}
            </button>
          ))}
        </nav>
        <div className="p-3 lg:mt-auto border-t border-slate-100 space-y-1">
          <a href="/" target="_blank" rel="noreferrer" className="flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-semibold text-[#334E68] hover:bg-slate-50"><ExternalLink size={18} /> View Website</a>
          <button onClick={doLogout} className="w-full flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-semibold text-red-500 hover:bg-red-50" data-testid="admin-logout"><LogOut size={18} /> Logout</button>
        </div>
      </aside>

      <main className="flex-1 p-4 sm:p-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-extrabold text-[#0F1E36] capitalize">{TABS.find((t) => t.id === tab)?.label}</h1>
            <p className="text-sm text-[#627D98]">Signed in as {user?.email}</p>
          </div>
        </div>
        {tab === "appointments" && <Appointments />}
        {tab === "doctors" && <Doctors />}
        {tab === "services" && <Services />}
        {tab === "gallery" && <Gallery />}
        {tab === "articles" && <Articles />}
        {tab === "testimonials" && <Testimonials />}
        {tab === "content" && <Content />}
      </main>
    </div>
  );
}

const useList = (key, url) => useQuery({ queryKey: [key], queryFn: async () => (await api.get(url)).data });
const Card = ({ children }) => <div className="bg-white rounded-2xl border border-slate-100 p-5">{children}</div>;
const AddBtn = (props) => <button {...props} className="gradient-bg btn-glow text-white font-bold text-sm px-5 py-2.5 rounded-full flex items-center gap-2"><Plus size={16} /> {props.children}</button>;

/* ---------------- Appointments ---------------- */
function Appointments() {
  const qc = useQueryClient();
  const { data: list = [] } = useList("appointments", "/appointments");
  const STATUSES = ["pending", "confirmed", "completed", "cancelled"];
  const colors = { pending: "bg-amber-100 text-amber-700", confirmed: "bg-blue-100 text-blue-700", completed: "bg-green-100 text-green-700", cancelled: "bg-red-100 text-red-700" };

  const setStatus = async (id, status) => { await api.patch(`/appointments/${id}`, { status }); qc.invalidateQueries({ queryKey: ["appointments"] }); toast.success("Status updated"); };
  const remove = async (id) => { if (!window.confirm("Delete this appointment?")) return; await api.delete(`/appointments/${id}`); qc.invalidateQueries({ queryKey: ["appointments"] }); toast.success("Deleted"); };

  if (!list.length) return <Card><p className="text-[#627D98] text-center py-10">No appointment requests yet.</p></Card>;
  return (
    <div className="space-y-3">
      {list.map((a) => (
        <Card key={a.id}>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-bold text-[#0F1E36]">{a.full_name}</span>
                <span className={`text-xs font-semibold px-2 py-0.5 rounded-full capitalize ${colors[a.status]}`}>{a.status}</span>
              </div>
              <div className="text-sm text-[#627D98] mt-1">{a.phone} {a.email && `· ${a.email}`}</div>
              <div className="text-sm text-[#627D98]">{a.service} {a.preferred_date && `· ${a.preferred_date} ${a.preferred_time}`}</div>
              {a.message && <div className="text-sm text-[#334E68] mt-1 italic">"{a.message}"</div>}
            </div>
            <div className="flex items-center gap-2">
              <select value={a.status} onChange={(e) => setStatus(a.id, e.target.value)} className="rounded-lg border border-slate-200 text-sm px-3 py-2" data-testid={`appt-status-${a.id}`}>
                {STATUSES.map((s) => <option key={s} value={s} className="capitalize">{s}</option>)}
              </select>
              <button onClick={() => remove(a.id)} className="text-red-400 hover:text-red-600 p-2"><Trash2 size={18} /></button>
            </div>
          </div>
        </Card>
      ))}
    </div>
  );
}

/* ---------------- Doctors ---------------- */
function Doctors() {
  const qc = useQueryClient();
  const { data: list = [] } = useList("doctors", "/doctors");
  const [editing, setEditing] = useState(null);
  const blank = { name: "", qualification: "", specialization: "", experience: "", languages: "", expertise: [], description: "", image: "", order: 0 };

  const save = async (d) => {
    if (d.id) await api.put(`/doctors/${d.id}`, d); else await api.post("/doctors", d);
    qc.invalidateQueries({ queryKey: ["doctors"] }); setEditing(null); toast.success("Saved");
  };
  const remove = async (id) => { if (!window.confirm("Delete this doctor?")) return; await api.delete(`/doctors/${id}`); qc.invalidateQueries({ queryKey: ["doctors"] }); toast.success("Deleted"); };

  return (
    <div>
      <div className="flex justify-end mb-4"><AddBtn onClick={() => setEditing(blank)} data-testid="add-doctor">Add Doctor</AddBtn></div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {list.map((d) => (
          <Card key={d.id}>
            <div className="flex gap-3">
              <img src={fileUrl(d.image)} alt={d.name} className="w-16 h-16 rounded-xl object-cover" />
              <div className="flex-1 min-w-0"><div className="font-bold text-[#0F1E36] truncate">{d.name}</div><div className="text-xs text-[#627D98] line-clamp-2">{d.specialization}</div></div>
            </div>
            <div className="flex gap-2 mt-4">
              <button onClick={() => setEditing(d)} className="flex-1 text-sm font-semibold text-[#1A75BC] bg-[#E8F4FD] rounded-lg py-2 flex items-center justify-center gap-1"><Pencil size={14} /> Edit</button>
              <button onClick={() => remove(d.id)} className="text-red-400 hover:text-red-600 px-3"><Trash2 size={16} /></button>
            </div>
          </Card>
        ))}
      </div>
      <DoctorModal doc={editing} onClose={() => setEditing(null)} onSave={save} />
    </div>
  );
}

function DoctorModal({ doc, onClose, onSave }) {
  const [f, setF] = useState(doc || {});
  React.useEffect(() => {
    setF(doc || {});
  }, [doc]);
  if (!doc) return null;
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <Modal open={!!doc} onClose={onClose} title={f.id ? "Edit Doctor" : "Add Doctor"}>
      <div className="space-y-3">
        <UploadField label="Doctor Photo" value={f.image} onChange={(url) => setF({ ...f, image: url })} />
        <input className={inputCls} placeholder="Name" value={f.name || ""} onChange={set("name")} data-testid="doctor-name" />
        <input className={inputCls} placeholder="Qualification" value={f.qualification || ""} onChange={set("qualification")} />
        <input className={inputCls} placeholder="Specialization" value={f.specialization || ""} onChange={set("specialization")} />
        <div className="grid grid-cols-2 gap-3">
          <input className={inputCls} placeholder="Experience" value={f.experience || ""} onChange={set("experience")} />
          <input className={inputCls} placeholder="Order" type="number" value={f.order || 0} onChange={(e) => setF({ ...f, order: Number(e.target.value) })} />
        </div>
        <input className={inputCls} placeholder="Languages Known" value={f.languages || ""} onChange={set("languages")} />
        <textarea className={inputCls} rows={2} placeholder="Areas of expertise (comma separated)" value={(f.expertise || []).join(", ")} onChange={(e) => setF({ ...f, expertise: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })} />
        <textarea className={inputCls} rows={4} placeholder="Description" value={f.description || ""} onChange={set("description")} />
        <button onClick={() => onSave(f)} className="w-full gradient-bg text-white font-bold py-3 rounded-full" data-testid="doctor-save">Save Doctor</button>
      </div>
    </Modal>
  );
}

/* ---------------- Services ---------------- */
function Services() {
  const qc = useQueryClient();
  const { data: list = [] } = useList("services", "/services");
  const [editing, setEditing] = useState(null);
  const blank = { title: "", icon: "stethoscope", short_description: "", description: "", category: "medical", order: 0 };
  const save = async (d) => { if (d.id) await api.put(`/services/${d.id}`, d); else await api.post("/services", d); qc.invalidateQueries({ queryKey: ["services"] }); setEditing(null); toast.success("Saved"); };
  const remove = async (id) => { if (!window.confirm("Delete service?")) return; await api.delete(`/services/${id}`); qc.invalidateQueries({ queryKey: ["services"] }); toast.success("Deleted"); };

  return (
    <div>
      <div className="flex justify-end mb-4"><AddBtn onClick={() => setEditing(blank)} data-testid="add-service">Add Service</AddBtn></div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {list.map((s) => (
          <Card key={s.id}>
            <div className="flex items-center justify-between"><span className="text-xs font-bold uppercase text-[#27AE60]">{s.category}</span></div>
            <div className="font-bold text-[#0F1E36] mt-1">{s.title}</div>
            <div className="text-xs text-[#627D98] line-clamp-2 mt-1">{s.short_description}</div>
            <div className="flex gap-2 mt-4">
              <button onClick={() => setEditing(s)} className="flex-1 text-sm font-semibold text-[#1A75BC] bg-[#E8F4FD] rounded-lg py-2 flex items-center justify-center gap-1"><Pencil size={14} /> Edit</button>
              <button onClick={() => remove(s.id)} className="text-red-400 hover:text-red-600 px-3"><Trash2 size={16} /></button>
            </div>
          </Card>
        ))}
      </div>
      {editing && <ServiceModal svc={editing} onClose={() => setEditing(null)} onSave={save} />}
    </div>
  );
}

function ServiceModal({ svc, onClose, onSave }) {
  const [f, setF] = useState(svc);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <Modal open onClose={onClose} title={f.id ? "Edit Service" : "Add Service"}>
      <div className="space-y-3">
        <input className={inputCls} placeholder="Title" value={f.title || ""} onChange={set("title")} data-testid="service-title" />
        <div className="grid grid-cols-2 gap-3">
          <select className={inputCls} value={f.category} onChange={set("category")}><option value="medical">Medical</option><option value="diagnostic">Diagnostic</option></select>
          <select className={inputCls} value={f.icon} onChange={set("icon")}>{ICON_OPTIONS.map((i) => <option key={i} value={i}>{i}</option>)}</select>
        </div>
        <input className={inputCls} placeholder="Order" type="number" value={f.order || 0} onChange={(e) => setF({ ...f, order: Number(e.target.value) })} />
        <textarea className={inputCls} rows={2} placeholder="Short description" value={f.short_description || ""} onChange={set("short_description")} />
        <textarea className={inputCls} rows={4} placeholder="Full description" value={f.description || ""} onChange={set("description")} />
        <button onClick={() => onSave(f)} className="w-full gradient-bg text-white font-bold py-3 rounded-full" data-testid="service-save">Save Service</button>
      </div>
    </Modal>
  );
}

/* ---------------- Gallery ---------------- */
function Gallery() {
  const qc = useQueryClient();
  const { data: list = [] } = useList("gallery", "/gallery");
  const [editing, setEditing] = useState(null);
  const blank = { title: "", description: "", category: "Clinic", media_type: "image", url: "" };
  const save = async (d) => { if (!d.url) { toast.error("Upload a file first"); return; } await api.post("/gallery", d); qc.invalidateQueries({ queryKey: ["gallery"] }); setEditing(null); toast.success("Published"); };
  const remove = async (id) => { if (!window.confirm("Delete media?")) return; await api.delete(`/gallery/${id}`); qc.invalidateQueries({ queryKey: ["gallery"] }); toast.success("Deleted"); };

  return (
    <div>
      <div className="flex justify-end mb-4"><AddBtn onClick={() => setEditing(blank)} data-testid="add-gallery">Add Media</AddBtn></div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
        {list.map((g) => (
          <div key={g.id} className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
            {g.media_type === "video" ? <video src={fileUrl(g.url)} className="w-full h-32 object-cover" /> : <img src={fileUrl(g.url)} alt={g.title} className="w-full h-32 object-cover" />}
            <div className="p-3">
              <div className="text-sm font-semibold text-[#0F1E36] truncate">{g.title || "Untitled"}</div>
              <div className="text-xs text-[#627D98]">{g.category}</div>
              <button onClick={() => remove(g.id)} className="text-red-400 hover:text-red-600 text-xs font-semibold mt-2 flex items-center gap-1"><Trash2 size={13} /> Delete</button>
            </div>
          </div>
        ))}
      </div>
      {editing && <GalleryModal item={editing} onClose={() => setEditing(null)} onSave={save} />}
    </div>
  );
}

function GalleryModal({ item, onClose, onSave }) {
  const [f, setF] = useState(item);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const cats = ["Clinic", "Posters", "Doctors", "Facilities", "Events"];
  return (
    <Modal open onClose={onClose} title="Add Gallery Media">
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <select className={inputCls} value={f.media_type} onChange={set("media_type")} data-testid="gallery-type"><option value="image">Image</option><option value="video">Video</option></select>
          <select className={inputCls} value={f.category} onChange={set("category")}>{cats.map((c) => <option key={c} value={c}>{c}</option>)}</select>
        </div>
        <UploadField label={f.media_type === "video" ? "Video" : "Image"} value={f.url} onChange={(url) => setF({ ...f, url })} accept={f.media_type === "video" ? "video/*" : "image/*"} />
        <input className={inputCls} placeholder="Title" value={f.title || ""} onChange={set("title")} data-testid="gallery-title" />
        <textarea className={inputCls} rows={2} placeholder="Description" value={f.description || ""} onChange={set("description")} />
        <button onClick={() => onSave(f)} className="w-full gradient-bg text-white font-bold py-3 rounded-full" data-testid="gallery-save">Publish</button>
      </div>
    </Modal>
  );
}

/* ---------------- Articles ---------------- */
function Articles() {
  const qc = useQueryClient();
  const { data: list = [] } = useList("articles", "/articles");
  const [editing, setEditing] = useState(null);
  const blank = { title: "", category: "Health Tips", excerpt: "", content: "", author: "Revive Family Clinic", image: "" };
  const save = async (d) => { if (d.id) await api.put(`/articles/${d.id}`, d); else await api.post("/articles", d); qc.invalidateQueries({ queryKey: ["articles"] }); setEditing(null); toast.success("Saved"); };
  const remove = async (id) => { if (!window.confirm("Delete article?")) return; await api.delete(`/articles/${id}`); qc.invalidateQueries({ queryKey: ["articles"] }); toast.success("Deleted"); };

  return (
    <div>
      <div className="flex justify-end mb-4"><AddBtn onClick={() => setEditing(blank)} data-testid="add-article">Add Article</AddBtn></div>
      <div className="space-y-3">
        {list.map((a) => (
          <Card key={a.id}>
            <div className="flex items-center gap-4">
              <img src={a.image ? fileUrl(a.image) : "https://images.unsplash.com/photo-1505751172876-fa1923c5c528?auto=format&fit=crop&w=200&q=60"} alt="" className="w-16 h-16 rounded-xl object-cover shrink-0" />
              <div className="flex-1 min-w-0"><div className="font-bold text-[#0F1E36] truncate">{a.title}</div><div className="text-xs text-[#627D98]">{a.category}</div></div>
              <button onClick={() => setEditing(a)} className="text-[#1A75BC] p-2"><Pencil size={17} /></button>
              <button onClick={() => remove(a.id)} className="text-red-400 hover:text-red-600 p-2"><Trash2 size={17} /></button>
            </div>
          </Card>
        ))}
      </div>
      {editing && <ArticleModal art={editing} onClose={() => setEditing(null)} onSave={save} />}
    </div>
  );
}

function ArticleModal({ art, onClose, onSave }) {
  const [f, setF] = useState(art);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <Modal open onClose={onClose} title={f.id ? "Edit Article" : "Add Article"}>
      <div className="space-y-3">
        <UploadField label="Featured Image" value={f.image} onChange={(url) => setF({ ...f, image: url })} />
        <input className={inputCls} placeholder="Title" value={f.title || ""} onChange={set("title")} data-testid="article-title" />
        <div className="grid grid-cols-2 gap-3">
          <input className={inputCls} placeholder="Category" value={f.category || ""} onChange={set("category")} />
          <input className={inputCls} placeholder="Author" value={f.author || ""} onChange={set("author")} />
        </div>
        <textarea className={inputCls} rows={2} placeholder="Excerpt / summary" value={f.excerpt || ""} onChange={set("excerpt")} />
        <textarea className={inputCls} rows={6} placeholder="Full article content" value={f.content || ""} onChange={set("content")} />
        <button onClick={() => onSave(f)} className="w-full gradient-bg text-white font-bold py-3 rounded-full" data-testid="article-save">Save Article</button>
      </div>
    </Modal>
  );
}

/* ---------------- Testimonials ---------------- */
function Testimonials() {
  const qc = useQueryClient();
  const { data: list = [] } = useList("testimonials", "/testimonials");
  const [editing, setEditing] = useState(null);
  const blank = { name: "", rating: 5, text: "", location: "", order: 0 };
  const save = async (d) => { if (d.id) await api.put(`/testimonials/${d.id}`, d); else await api.post("/testimonials", d); qc.invalidateQueries({ queryKey: ["testimonials"] }); setEditing(null); toast.success("Saved"); };
  const remove = async (id) => { if (!window.confirm("Delete review?")) return; await api.delete(`/testimonials/${id}`); qc.invalidateQueries({ queryKey: ["testimonials"] }); toast.success("Deleted"); };

  return (
    <div>
      <div className="flex justify-end mb-4"><AddBtn onClick={() => setEditing(blank)} data-testid="add-testimonial">Add Review</AddBtn></div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {list.map((t) => (
          <Card key={t.id}>
            <div className="flex gap-0.5 mb-2">{Array.from({ length: t.rating }).map((_, i) => <Star key={i} size={15} className="text-amber-400 fill-amber-400" />)}</div>
            <p className="text-sm text-[#334E68] line-clamp-3">"{t.text}"</p>
            <div className="text-sm font-bold text-[#0F1E36] mt-2">{t.name} <span className="text-xs font-normal text-[#627D98]">· {t.location}</span></div>
            <div className="flex gap-2 mt-3">
              <button onClick={() => setEditing(t)} className="flex-1 text-sm font-semibold text-[#1A75BC] bg-[#E8F4FD] rounded-lg py-2 flex items-center justify-center gap-1"><Pencil size={14} /> Edit</button>
              <button onClick={() => remove(t.id)} className="text-red-400 hover:text-red-600 px-3"><Trash2 size={16} /></button>
            </div>
          </Card>
        ))}
      </div>
      {editing && <TestimonialModal t={editing} onClose={() => setEditing(null)} onSave={save} />}
    </div>
  );
}

function TestimonialModal({ t, onClose, onSave }) {
  const [f, setF] = useState(t);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <Modal open onClose={onClose} title={f.id ? "Edit Review" : "Add Review"}>
      <div className="space-y-3">
        <input className={inputCls} placeholder="Patient Name" value={f.name || ""} onChange={set("name")} data-testid="testimonial-name" />
        <div className="grid grid-cols-2 gap-3">
          <input className={inputCls} placeholder="Location" value={f.location || ""} onChange={set("location")} />
          <select className={inputCls} value={f.rating} onChange={(e) => setF({ ...f, rating: Number(e.target.value) })}>{[5, 4, 3, 2, 1].map((r) => <option key={r} value={r}>{r} Stars</option>)}</select>
        </div>
        <textarea className={inputCls} rows={4} placeholder="Review text" value={f.text || ""} onChange={set("text")} data-testid="testimonial-text" />
        <button onClick={() => onSave(f)} className="w-full gradient-bg text-white font-bold py-3 rounded-full" data-testid="testimonial-save">Save Review</button>
      </div>
    </Modal>
  );
}

/* ---------------- Site Content ---------------- */
function Content() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["content"], queryFn: async () => (await api.get("/content")).data });
  const [f, setF] = useState(null);
  React.useEffect(() => { if (data && !f) setF(data); }, [data]); // eslint-disable-line
  if (!f) return <Card><p className="text-[#627D98]">Loading...</p></Card>;
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const save = async () => { await api.put("/content", f); qc.invalidateQueries({ queryKey: ["content"] }); toast.success("Website content updated"); };

  const Field = ({ label, k, textarea, ...rest }) => (
    <div>
      <label className="text-sm font-semibold text-[#334E68]">{label}</label>
      {textarea ? <textarea className={inputCls + " mt-1"} rows={3} value={f[k] || ""} onChange={set(k)} {...rest} /> : <input className={inputCls + " mt-1"} value={f[k] || ""} onChange={set(k)} {...rest} />}
    </div>
  );

  return (
    <div className="space-y-6 max-w-3xl">
      <Card>
        <h3 className="font-bold text-[#0F1E36] mb-4">About Content</h3>
        <div className="space-y-3">
          <Field label="Established Year" k="established" />
          <Field label="Our Story" k="story" textarea />
          <Field label="Mission" k="mission" textarea />
          <Field label="Vision" k="vision" textarea />
          <Field label="Healthcare Philosophy" k="philosophy" textarea />
        </div>
      </Card>
      <Card>
        <h3 className="font-bold text-[#0F1E36] mb-4">Contact Details</h3>
        <div className="grid sm:grid-cols-2 gap-3">
          <Field label="Phone" k="phone" />
          <Field label="WhatsApp (digits only, with country code)" k="whatsapp" />
          <Field label="Email" k="email" />
          <Field label="Address" k="address" />
          <Field label="Opening Hours" k="hours" />
          <Field label="Google Maps Embed URL" k="map_embed" />
          <Field label="Google Maps Link (View on Google)" k="map_url" />
        </div>
      </Card>
      <Card>
        <h3 className="font-bold text-[#0F1E36] mb-1">Google Reviews</h3>
        <p className="text-xs text-[#627D98] mb-4">Shown on the Patient Reviews page. Update the rating and count from your Google Business profile.</p>
        <div className="grid sm:grid-cols-3 gap-3">
          <Field label="Google Rating (e.g. 4.9)" k="google_rating" />
          <Field label="Number of Google Reviews" k="google_reviews_count" />
          <Field label="Google Reviews URL" k="google_reviews_url" />
        </div>
      </Card>
      <Card>
        <h3 className="font-bold text-[#0F1E36] mb-4">Social Media Links</h3>
        <div className="grid sm:grid-cols-3 gap-3">
          <Field label="Instagram" k="instagram" />
          <Field label="Facebook" k="facebook" />
          <Field label="YouTube" k="youtube" />
        </div>
      </Card>
      <Card>
        <h3 className="font-bold text-[#0F1E36] mb-1">Statistics (Counters)</h3>
        <p className="text-xs text-[#627D98] mb-4">Shown on the Home and About pages. Value is a number; suffix is e.g. "+", "%".</p>
        <div className="space-y-3">
          {(f.stats || []).map((st, i) => (
            <div key={i} className="grid grid-cols-12 gap-2 items-center">
              <input className={inputCls + " col-span-6"} placeholder="Label" value={st.label || ""} onChange={(e) => { const s = [...f.stats]; s[i] = { ...s[i], label: e.target.value }; setF({ ...f, stats: s }); }} data-testid={`stat-label-${i}`} />
              <input className={inputCls + " col-span-3"} type="number" placeholder="Value" value={st.value ?? ""} onChange={(e) => { const s = [...f.stats]; s[i] = { ...s[i], value: Number(e.target.value) }; setF({ ...f, stats: s }); }} data-testid={`stat-value-${i}`} />
              <input className={inputCls + " col-span-2"} placeholder="Suffix" value={st.suffix || ""} onChange={(e) => { const s = [...f.stats]; s[i] = { ...s[i], suffix: e.target.value }; setF({ ...f, stats: s }); }} data-testid={`stat-suffix-${i}`} />
              <button type="button" onClick={() => setF({ ...f, stats: f.stats.filter((_, j) => j !== i) })} className="col-span-1 text-slate-400 hover:text-red-500 flex justify-center" title="Remove" data-testid={`stat-remove-${i}`}><Trash2 size={18} /></button>
            </div>
          ))}
          <button type="button" onClick={() => setF({ ...f, stats: [...(f.stats || []), { label: "New Stat", value: 0, suffix: "+" }] })} className="text-sm font-bold text-[#1A75BC]" data-testid="stat-add">+ Add Statistic</button>
        </div>
      </Card>
      <button onClick={save} className="gradient-bg btn-glow text-white font-bold px-8 py-3 rounded-full" data-testid="content-save">Save All Changes</button>
    </div>
  );
}
